import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Typography,
  TableContainer,
  Table,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Button,
  Grid,
  CardHeader,
  Skeleton,
  Tooltip,
  TableFooter,
  createTheme,
  Collapse,
  IconButton,
  Chip,
  Box,
  TableHead,
} from "@material-ui/core";
import { ThemeProvider, makeStyles } from "@material-ui/styles";
import VisibilityIcon from "@material-ui/icons/Visibility";
import * as locales from "@material-ui/core/locale";
import { propuestasApi } from "src/services/propuestasApi";
import { TablaHead, TablaToolbar } from "../_dashboard/tablas";
import { applySortFilter, getComparator } from "src/utils/sharedTables";
import Scrollbar from "../Scrollbar";
import { capitalizarPalabras } from "src/utils/sharedFunctions";
import SearchNotFound from "../SearchNotFound";
import CustomTablePagination from "../CustomTablePagination";
import { formatearFecha } from "src/utils/formatTime";
import { useNavigate } from "react-router-dom";
import ProposalStatusLabel from "../material/ProposalStatusLabel";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: 275,
  },
  pos: {
    marginBottom: 12,
  },
}));

const INITIAL_STATE_TABLE = {
  currentPage: 0,
  rowsPerPageOptions: 10,
  order: "desc",
  orderBy: "fechaCreacion",
  locale: "esES",
  filterName: "",
};

const TABLE_HEAD = [
  { id: "position", label: "N°", alignRight: false },
  { id: "tipo", label: "Tipo", alignRight: false },
  { id: "servicios", label: "Servicios", alignRight: false },
  { id: "nombreVendedor", label: "Vendedor", alignRight: false },
  { id: "fechaCreacion", label: "Fecha Creación", alignRight: false },
  { id: "fechaEnvioCorreo", label: "Fecha Envío", alignRight: false },
  { id: "estado", label: "Estado", alignRight: false },
  { id: "" },
];

export default function PropuestasAsociadasCliente(props) {
  const { value, cliente, title, subheader } = props;
  const classes = useStyles();
  const navigate = useNavigate();

  const [statesTable, setStateTable] = useState(INITIAL_STATE_TABLE);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const { data: propuestas = [], isLoading: cargandoPropuestas } =
    propuestasApi.useGetPropuestasClienteQuery(
      { rutCliente: cliente.rut },
      { skip: !cliente.rut },
    );

  const handleToggleExpanded = (propuestaId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(propuestaId)) {
        newSet.delete(propuestaId);
      } else {
        newSet.add(propuestaId);
      }
      return newSet;
    });
  };

  const handleFilterByName = (event) => {
    const filterName = event.target.value;
    const newTotalPages = Math.ceil(
      rowsFiltradas.length / statesTable.rowsPerPageOptions,
    );
    let newCurrentPage = statesTable.currentPage;
    if (newTotalPages < statesTable.currentPage) {
      newCurrentPage = newTotalPages - 1;
    }

    setStateTable({
      ...statesTable,
      filterName,
      currentPage: newCurrentPage,
    });
  };

  const rowsFiltradas = useMemo(() => {
    if (!statesTable.filterName.trim()) {
      return applySortFilter(
        propuestas,
        getComparator(statesTable.order, statesTable.orderBy),
        "",
        [],
      );
    }

    // Filtrar propuestas que coincidan en campos básicos O en servicios
    const propuestasFiltradas = propuestas.filter((propuesta) => {
      const filterTerm = statesTable.filterName.toLowerCase();

      // Buscar en campos básicos
      const basicFields = [
        propuesta.tipo,
        propuesta.clienteNombre,
        propuesta.nombreVendedor,
        propuesta.estado,
      ];

      const matchBasicFields = basicFields.some((field) =>
        field?.toLowerCase().includes(filterTerm),
      );

      // Buscar en servicios
      const matchServices = propuesta.servicios.some((servicio) =>
        servicio.nombre?.toLowerCase().includes(filterTerm),
      );

      return matchBasicFields || matchServices;
    });

    // Aplicar ordenamiento a los resultados filtrados
    return applySortFilter(
      propuestasFiltradas,
      getComparator(statesTable.order, statesTable.orderBy),
      "", // filterName vacío porque ya filtramos arriba
      [], // propiedadesParaFiltrar vacío porque ya filtramos arriba
    );
  }, [
    statesTable.filterName,
    statesTable.order,
    statesTable.orderBy,
    propuestas,
  ]);

  const handleRequestSort = (event, property) => {
    const isAsc =
      statesTable.orderBy === property && statesTable.order === "asc";
    setStateTable({
      ...statesTable,
      order: isAsc ? "desc" : "asc",
      orderBy: property,
    });
  };

  // Efecto para ajustar la página cuando los resultados filtrados cambian
  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(rowsFiltradas.length / statesTable.rowsPerPageOptions) - 1,
    );

    if (statesTable.currentPage > maxPage) {
      setStateTable((prev) => ({
        ...prev,
        currentPage: maxPage,
      }));
    }
  }, [
    rowsFiltradas.length,
    statesTable.currentPage,
    statesTable.rowsPerPageOptions,
  ]);

  const handleChangePage = (event, newPage) => {
    const maxPage = Math.max(
      0,
      Math.ceil(rowsFiltradas.length / statesTable.rowsPerPageOptions) - 1,
    );

    setStateTable({
      ...statesTable,
      currentPage: Math.min(newPage, maxPage),
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setStateTable({
      ...statesTable,
      rowsPerPageOptions: Number(event.target.value),
      currentPage: 0,
    });
  };

  const seEncontroElemento = rowsFiltradas.length === 0;

  const filasVacias =
    statesTable.currentPage > 0
      ? Math.min(
          0,
          (1 + statesTable.currentPage) * statesTable.rowsPerPageOptions -
            propuestas.length,
        )
      : 0;

  const handleClickWatchProposal = (propuesta) => {
    if (propuesta.tipo === "V2") {
      navigate(`/dashboard/propuestas/v2/${propuesta.id}`);
    } else {
      navigate(`/dashboard/propuestas/${propuesta.tipo}/${propuesta.id}`);
    }
  };

  return (
    <div className={classes.pos}>
      {value === 8 && (
        <Card className={classes.root} variant="outlined">
          <Grid container>
            <Grid item xs={12} md={8} lg={8}>
              <CardHeader title={title} subheader={subheader} />
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              lg={4}
              sx={{
                display: "grid",
                justifyContent: { xs: "center", md: "right", lg: "right" },
                alignItems: "flex-end",
              }}
            >
              <TablaToolbar
                filterName={statesTable.filterName}
                onFilterName={handleFilterByName}
                placeholder="Buscar por tipo, vendedor, estado o servicio..."
              />
            </Grid>
          </Grid>

          <Scrollbar>
            <TableContainer component={Paper}>
              {!cargandoPropuestas ? (
                <Table
                  sx={{ minWidth: classes.root }}
                  size="small"
                  aria-label="tabla con propuestas cliente"
                >
                  <TablaHead
                    order={statesTable.order}
                    orderBy={statesTable.orderBy}
                    headLabel={TABLE_HEAD}
                    rowCount={propuestas.length}
                    onRequestSort={handleRequestSort}
                  />
                  <TableBody>
                    {rowsFiltradas
                      .slice(
                        statesTable.currentPage *
                          statesTable.rowsPerPageOptions,
                        statesTable.currentPage *
                          statesTable.rowsPerPageOptions +
                          statesTable.rowsPerPageOptions,
                      )
                      .map((row, index) => {
                        const isExpanded = expandedRows.has(row.id);
                        return (
                          <React.Fragment key={row.id}>
                            {/* Fila principal */}
                            <TableRow hover>
                              <TableCell>
                                {statesTable.currentPage *
                                  statesTable.rowsPerPageOptions +
                                  index +
                                  1}
                              </TableCell>
                              <TableCell>
                                <Typography variant="subtitle2">
                                  {row.tipo}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Chip
                                    label={`${row.servicios.length} servicio${
                                      row.servicios.length > 1 ? "s" : ""
                                    }`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => handleToggleExpanded(row.id)}
                                    aria-expanded={isExpanded}
                                  >
                                    {isExpanded ? (
                                      <ExpandLessIcon />
                                    ) : (
                                      <ExpandMoreIcon />
                                    )}
                                  </IconButton>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {capitalizarPalabras(row.nombreVendedor)}
                              </TableCell>
                              <TableCell>
                                {row.fechaCreacion
                                  ? formatearFecha(
                                      row.fechaCreacion,
                                      "DD/MM/YYYY",
                                    )
                                  : ""}
                              </TableCell>
                              <TableCell>
                                {row.fechaEnvioCorreo
                                  ? formatearFecha(
                                      row.fechaEnvioCorreo,
                                      "DD/MM/YYYY",
                                    )
                                  : "No enviada"}
                              </TableCell>
                              <TableCell>
                                <ProposalStatusLabel estado={row.estado} />
                              </TableCell>
                              <TableCell>
                                <Tooltip title="Ver propuesta">
                                  <Button
                                    color="primary"
                                    onClick={() =>
                                      handleClickWatchProposal(row)
                                    }
                                  >
                                    <VisibilityIcon />
                                  </Button>
                                </Tooltip>
                              </TableCell>
                            </TableRow>

                            {/* Fila expandible para mostrar servicios (para todos los tipos) */}
                            <TableRow>
                              <TableCell
                                style={{ paddingBottom: 0, paddingTop: 0 }}
                                colSpan={8}
                              >
                                <Collapse
                                  in={isExpanded}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Box margin={2}>
                                    <Typography
                                      variant="h6"
                                      gutterBottom
                                      component="div"
                                    >
                                      {row.servicios.length === 1
                                        ? "Servicio incluido"
                                        : "Servicios incluidos"}
                                    </Typography>
                                    <Table size="small" aria-label="servicios">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>
                                            <strong>Servicio</strong>
                                          </TableCell>
                                          <TableCell align="right">
                                            <strong>Valor Original</strong>
                                          </TableCell>
                                          <TableCell align="right">
                                            <strong>Descuento</strong>
                                          </TableCell>
                                          <TableCell align="right">
                                            <strong>Valor Final</strong>
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {row.servicios.map(
                                          (servicio, servicioIndex) => (
                                            <TableRow key={servicioIndex}>
                                              <TableCell>
                                                {capitalizarPalabras(
                                                  servicio.nombre,
                                                )}
                                              </TableCell>
                                              <TableCell align="right">
                                                {servicio.valor}{" "}
                                                {servicio.tipoMoneda}
                                              </TableCell>
                                              <TableCell align="right">
                                                {servicio.descuento}%
                                              </TableCell>
                                              <TableCell align="right">
                                                {/* Calcular valor final considerando el descuento */}
                                                {servicio.descuento > 0
                                                  ? (
                                                      servicio.valor *
                                                      (1 -
                                                        servicio.descuento /
                                                          100)
                                                    ).toFixed(2)
                                                  : servicio.valor}{" "}
                                                {servicio.tipoMoneda}
                                              </TableCell>
                                            </TableRow>
                                          ),
                                        )}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}

                    {filasVacias > 0 && (
                      <TableRow style={{ height: 53 * filasVacias }}>
                        <TableCell colSpan={8} />
                      </TableRow>
                    )}
                  </TableBody>
                  {seEncontroElemento && (
                    <TableBody>
                      <TableRow>
                        <TableCell align="center" colSpan={8} sx={{ py: 3 }}>
                          <SearchNotFound
                            searchQuery={statesTable.filterName}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                  <TableFooter>
                    <TableRow>
                      <ThemeProvider
                        theme={(outerTheme) =>
                          createTheme(outerTheme, locales[statesTable.locale])
                        }
                      >
                        <CustomTablePagination
                          count={rowsFiltradas.length}
                          rowsPerPage={statesTable.rowsPerPageOptions}
                          page={statesTable.currentPage}
                          colSpan={8}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                      </ThemeProvider>
                    </TableRow>
                  </TableFooter>
                </Table>
              ) : (
                <Skeleton variant="rectangular" width="100%" height="500px" />
              )}
            </TableContainer>
          </Scrollbar>
        </Card>
      )}
    </div>
  );
}
